/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Aviso compacto: ícono + detalle bajo demanda.
 * Web: tooltip al hover (portal, no recortado por scroll).
 * Mobile: tap abre toast breve.
 */
import React, { useCallback, useRef, useState } from 'react';
import {
  Platform,
  Pressable,
  StyleSheet,
  View,
  type GestureResponderEvent,
} from 'react-native';
import { createPortal } from 'react-dom';
import { Ionicons } from '@expo/vector-icons';
import { colors } from '../../shared/theme/public-api';
import { showInfo } from './appToast';

export type OperationalHintSeverity = 'info' | 'warning' | 'critical';

type AnchorRect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

type OperationalHintIconProps = {
  message: string;
  title?: string;
  severity?: OperationalHintSeverity;
  size?: number;
  testID?: string;
};

type WebOperationalHintTooltipProps = {
  visible: boolean;
  anchorRect: AnchorRect | null;
  title: string;
  message: string;
};

function readAnchorRect(node: View | null): AnchorRect | null {
  if (!node || Platform.OS !== 'web') return null;
  const domNode = node as unknown as HTMLElement | null;
  if (!domNode?.getBoundingClientRect) return null;
  const rect = domNode.getBoundingClientRect();
  return {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
  };
}

function WebOperationalHintTooltip({ visible, anchorRect, title, message }: WebOperationalHintTooltipProps) {
  if (Platform.OS !== 'web' || !visible || !anchorRect || typeof document === 'undefined') {
    return null;
  }

  const centerX = anchorRect.left + anchorRect.width / 2;
  const tooltipStyle: React.CSSProperties = {
    position: 'fixed',
    top: Math.max(8, anchorRect.top - 8),
    left: centerX,
    transform: 'translate(-50%, -100%)',
    maxWidth: 280,
    zIndex: 100_000,
    borderRadius: 10,
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.surface,
    padding: '8px 12px',
    pointerEvents: 'none',
    boxSizing: 'border-box',
  };

  return createPortal(
    <div
      role="tooltip"
      aria-label={`${title}. ${message}`}
      style={tooltipStyle}
    >
      <div
        style={{
          color: colors.textPrimary,
          fontSize: 13,
          fontWeight: 700,
          lineHeight: '16px',
          marginBottom: 2,
        }}
      >
        {title}
      </div>
      <div
        style={{
          color: colors.textSecondary,
          fontSize: 13,
          lineHeight: '18px',
          whiteSpace: 'pre-line',
        }}
      >
        {message}
      </div>
    </div>,
    document.body
  );
}

export function OperationalHintIcon({
  message,
  title = 'Aviso',
  severity = 'warning',
  size = 16,
  testID,
}: OperationalHintIconProps) {
  const anchorRef = useRef<View>(null);
  const [webTooltipVisible, setWebTooltipVisible] = useState(false);
  const [anchorRect, setAnchorRect] = useState<AnchorRect | null>(null);

  const iconName =
    severity === 'critical' ? 'alert-circle-outline'
    : severity === 'info' ? 'information-circle-outline'
    : 'warning-outline';
  const iconColor =
    severity === 'critical' ? colors.danger
    : severity === 'info' ? colors.textSecondary
    : colors.warning;

  const syncAnchorRect = useCallback(() => {
    setAnchorRect(readAnchorRect(anchorRef.current));
  }, []);

  const showWebTooltip = useCallback(() => {
    if (Platform.OS !== 'web') return;
    syncAnchorRect();
    setWebTooltipVisible(true);
  }, [syncAnchorRect]);

  const hideWebTooltip = useCallback(() => {
    if (Platform.OS !== 'web') return;
    setWebTooltipVisible(false);
  }, []);

  const handlePress = (event?: GestureResponderEvent) => {
    event?.stopPropagation?.();
    hideWebTooltip();
    if (Platform.OS !== 'web') {
      showInfo(title, message);
    }
  };

  return (
    <>
      <View ref={anchorRef} style={styles.wrapper} collapsable={false}>
        <Pressable
          hitSlop={8}
          style={styles.hitTarget}
          testID={testID}
          onPress={handlePress}
          onHoverIn={showWebTooltip}
          onHoverOut={hideWebTooltip}
          onFocus={showWebTooltip}
          onBlur={hideWebTooltip}
          accessibilityRole={Platform.OS === 'web' ? 'button' : 'button'}
          accessibilityLabel={`${title}. ${message}`}
          accessibilityHint="Ver el detalle"
        >
          <Ionicons name={iconName} size={size} color={iconColor} />
        </Pressable>
      </View>
      <WebOperationalHintTooltip
        visible={webTooltipVisible}
        anchorRect={anchorRect}
        title={title}
        message={message}
      />
    </>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    alignSelf: 'center',
  },
  hitTarget: {
    padding: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
